import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Divider,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Alert,
  useTheme,
  Avatar,
  Badge
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  Info as InfoIcon,
  Psychology as PsychologyIcon,
  EditNote as EditNoteIcon,
  Bolt as BoltIcon,
  Event as EventIcon,
  Settings as SettingsIcon,
  DoneAll as DoneAllIcon,
  Close as CloseIcon,
  OpenInFull as OpenInFullIcon,
  CloseFullscreen as CloseFullscreenIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon
} from '@mui/icons-material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AgentHelpModal, { AgentHelpTopic } from './AgentHelpModal';
import { AutoFixHigh as AutoFixHighIcon } from '@mui/icons-material';
import { Agent, AgentPrompt, AgentModel, TypeProduct, AgentStatus, RandomicoSchedule } from '../../types/agent';
import { agentApi } from '../../utils/agentApi';
import { segmentApi, Segment } from '../../utils/segmentApi';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { RichTextEditor } from '../RichTextEditor';
import TokenCounter from '../TokenCounter';
import { validateTokenLimit, calculateTokenInfo } from '../../utils/tokenCounter';
import { AI_MODELS } from '../../constants/aiModels';
import FileManager from '../FileManager';
import { useTranslation } from 'react-i18next';

interface AgentFormProps {
  onSave: (agent: Agent) => void;
  onCancel: () => void;
  agentId?: string;
  initialData?: Partial<Agent>;
}

// Styled components for graph visualization
const GraphCanvas = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  minHeight: '70vh',
  background: theme.palette.mode === 'dark' 
    ? '#1a1a1a'
    : '#f5f7fa',
  borderRadius: 16,
  padding: theme.spacing(6),
  overflow: 'hidden',
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}`,
  boxShadow: theme.palette.mode === 'dark'
    ? '0 8px 32px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.03)'
    : '0 8px 32px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
  // Dot grid pattern (N8N style)
  backgroundImage: theme.palette.mode === 'dark'
    ? 'radial-gradient(circle, rgba(255, 255, 255, 0.08) 1px, transparent 1px)'
    : 'radial-gradient(circle, rgba(0, 0, 0, 0.08) 1px, transparent 1px)',
  backgroundSize: '24px 24px',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,

    pointerEvents: 'none',
    zIndex: 0,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    pointerEvents: 'none',
    zIndex: 0,
  },
}));

const GraphNode = styled(Box)<{ color?: string; required?: boolean; isEditing?: boolean; completed?: boolean }>(({ theme, color, required, isEditing, completed }) => ({
  position: 'absolute',
  cursor: 'pointer',
  width: 140,
  height: 140,
  borderRadius: '50%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.3s ease',
  zIndex: 2,
  [theme.breakpoints.down('md')]: {
    width: 120,
    height: 120,
  },
  [theme.breakpoints.down('sm')]: {
    width: 100,
    height: 100,
  },
  background: theme.palette.mode === 'dark'
    ? '#1a1a1a'
    : 'radial-gradient(circle at 30% 30%, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.85))',
  border: `2px solid ${required && !isEditing && !completed ? 'rgba(244, 67, 54, 0.6)' : theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.1)'}`,
  boxShadow: theme.palette.mode === 'dark'
    ? required && !isEditing && !completed ? '0 6px 20px rgba(244, 67, 54, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.05)' : '0 6px 16px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.05)'
    : required && !isEditing && !completed ? '0 6px 20px rgba(244, 67, 54, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.8)' : '0 6px 16px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
  backdropFilter: 'blur(8px)',
  ...(required && !isEditing && !completed ? {
      '&::before': {
        content: '"Obrigatório"',
      position: 'absolute',
      top: '-30px',
      left: '50%',
      transform: 'translateX(-50%)',
      background: theme.palette.mode === 'dark' ? 'rgba(244, 67, 54, 0.9)' : 'rgba(244, 67, 54, 0.95)',
      color: 'white',
      fontSize: '0.7rem',
      fontWeight: 700,
      padding: '4px 12px',
      borderRadius: '12px',
      whiteSpace: 'nowrap',
      zIndex: 4,
      boxShadow: '0 4px 12px rgba(244, 67, 54, 0.4)',
      pointerEvents: 'none',
    },
  } : {}),
  '&:hover': {
    boxShadow: `0 8px 24px ${color}25`,
    borderColor: required && !isEditing && !completed ? '#F44336' : color,
    borderWidth: required && !isEditing && !completed ? '3px' : '2px',
    zIndex: 10,
  },
  '& .node-icon-container': {
    width: 56,
    height: 56,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: `linear-gradient(135deg, ${color}25, ${color}15)`,
    marginBottom: theme.spacing(0.5),
    transition: 'all 0.3s ease',
    [theme.breakpoints.down('md')]: {
      width: 48,
      height: 48,
    },
    [theme.breakpoints.down('sm')]: {
      width: 40,
      height: 40,
    },
  },
  '&:hover .node-icon-container': {
    transform: 'scale(1.1)',
    boxShadow: `0 4px 12px ${color}35`,
  },
  '& .node-icon': {
    color: color || theme.palette.primary.main,
    fontSize: '24px !important',
    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
  },
  '& .node-label': {
    fontWeight: 600,
    fontSize: '0.875rem',
    textAlign: 'center',
    color: theme.palette.text.primary,
    lineHeight: 1.3,
    maxWidth: '100%',
    wordBreak: 'break-word',
    [theme.breakpoints.down('md')]: {
      fontSize: '0.75rem',
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.7rem',
      lineHeight: 1.2,
    },
  },
  '& .node-badge': {
    position: 'absolute',
    top: -6,
    right: -6,
    zIndex: 3,
  },
}));

const ConnectionLine = styled('svg')<{ x1: number; y1: number; x2: number; y2: number; color?: string }>(
  ({ x1, y1, x2, y2, color, theme }) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
    zIndex: 1,
    '& line': {
      stroke: color || theme.palette.primary.main,
      strokeWidth: 2,
      strokeDasharray: '5,5',
      opacity: 0.4,
      animation: 'dashMove 20s linear infinite',
    },
    '@keyframes dashMove': {
      '0%': {
        strokeDashoffset: 0,
      },
      '100%': {
        strokeDashoffset: 1000,
      },
    },
  })
);

const AgentForm: React.FC<AgentFormProps> = ({ onSave, onCancel, agentId, initialData }) => {
  const theme = useTheme();
  const { user } = useAuth();
  const { t } = useTranslation();
  const isEditing = Boolean(agentId);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [promptDialog, setPromptDialog] = useState(false);
  const [currentPrompt, setCurrentPrompt] = useState<AgentPrompt>({ role: 'assistant', content: '' });
  const [editingPromptIndex, setEditingPromptIndex] = useState<number | null>(null);
  const [promptFilter, setPromptFilter] = useState('');
  const [improveDialogOpen, setImproveDialogOpen] = useState(false);
  const [improveTargetIndex, setImproveTargetIndex] = useState<number | null>(null);
  const [improveInput, setImproveInput] = useState('');
  const [improveLoading, setImproveLoading] = useState(false);
  const [brainImproveDialogOpen, setBrainImproveDialogOpen] = useState(false);
  const [brainImproveInput, setBrainImproveInput] = useState('');
  const [brainImproveLoading, setBrainImproveLoading] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [helpTopic, setHelpTopic] = useState<AgentHelpTopic>('basic-info');
  const [expandedPrompts, setExpandedPrompts] = useState<Set<number>>(new Set());
  
  // Modal states for each section
  const [openModal, setOpenModal] = useState<string | null>(null);
  const [segments, setSegments] = useState<Segment[]>([]);

  const [formData, setFormData] = useState<Partial<Agent>>({
    name: '',
    description: '',
    model: 'gpt-4o-mini',
    typeProduct: 'Lead',
    status: 'Enabled',
    responseDelay: 0,
    exigirNome: false,
    exigirNomeCompleto: false,
    naoAcumularMensagens: false,
    encerramento: false,
    descricaoEncerramento: '',
    bancoCRM: '0',
    statusCollectionName: false,
    nomeCollectionName: '',
    numbersToHomologate: '',
    shouldRedirectLeads: false,
    shouldBlockAI: false,
    useMostPowerfulModel: false,
    mensagemRetornoRandomicoDisabled: '',
    clienteId: 0,
    prompts: [],
    chatHistory: [],
    randomicoSchedule: [],
    randomicoPersonalizado: [],
    aiActions: [],
    agentConfig: {
      routingMode: 'disabled',
      followUp: 0,
      followUpDescription: '',
      statusAgentConfig: 'disabled',
      sendBussinessHours: false,
      crmConfiguration: {
        enabled: false,
        url: '',
        method: 'POST',
        headers: [{ key: 'Content-Type', value: 'application/json' }],
        fieldMappings: [],
        customBody: ''
      },
      enviarLeedCRM: false,
      urlLeedCRM: '',
      TokenLeedCRM: '',
      emailEntradaLeed: ''
    }
  });

  // Node configurations with positions and colors (circular layout)
  const getNodesConfig = () => {
    return [
      {
        id: 'brain',
        label: t('agents.form.brain'),
        icon: <PsychologyIcon />,
        color: '#9C27B0',
        position: { x: 50, y: 50 },
        getBadge: () => null,
        required: true,
      },
      {
        id: 'basic-info',
        label: t('agents.form.basicInfo'),
        icon: <InfoIcon />,
        color: '#2196F3',
        position: { x: 32, y: 35 },
        getBadge: () => null,
        required: true,
      },
      {
        id: 'prompts',
        label: t('agents.form.prompts'),
        icon: <EditNoteIcon />,
        color: '#FF9800',
        position: { x: 50, y: 22 },
        getBadge: () => formData.prompts?.length || 0,
      },
      {
        id: 'ai-actions',
        label: t('agents.form.aiActions'),
        icon: <BoltIcon />,
        color: '#F44336',
        position: { x: 68, y: 35 },
        getBadge: () => formData.aiActions?.length || 0,
      },
      {
        id: 'scheduling',
        label: t('agents.form.scheduling'),
        icon: <EventIcon />,
        color: '#4CAF50',
        position: { x: 32, y: 62 },
        getBadge: () => formData.randomicoSchedule?.length || 0,
      },
      {
        id: 'settings',
        label: t('agents.form.settings'),
        icon: <SettingsIcon />,
        color: '#607D8B',
        position: { x: 50, y: 77 },
        getBadge: () => null,
      },
      {
        id: 'review',
        label: t('agents.form.review'),
        icon: <DoneAllIcon />,
        color: '#00BCD4',
        position: { x: 68, y: 62 },
        getBadge: () => null,
      },
    ];
  };

  const nodesConfig = getNodesConfig();

  // Check if a section is completed
  const isSectionCompleted = (sectionId: string): boolean => {
    switch (sectionId) {
      case 'basic-info':
        return !!(formData.name?.trim());
      case 'brain':
        const descriptionText = (() => {
          const d = document.createElement('div');
          d.innerHTML = formData.description || '';
          return (d.textContent || '').trim();
        })();
        return !!descriptionText;
      case 'prompts':
        return (formData.prompts?.length || 0) > 0;
      case 'ai-actions':
        return (formData.aiActions?.length || 0) > 0;
      case 'scheduling':
        return (formData.randomicoSchedule?.length || 0) > 0;
      case 'settings':
        return true; // Settings are always valid
      case 'review':
        return canSaveForm();
      default:
        return false;
    }
  };

  useEffect(() => {
    if (isEditing && agentId) {
      loadAgent(agentId);
    }
  }, [isEditing, agentId]);

  useEffect(() => {
    if (!isEditing && initialData) {
      setFormData(prev => ({ ...prev, ...initialData }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData, isEditing]);

  useEffect(() => {
    const loadSegments = async () => {
      try {
        const rs = await segmentApi.list();
        setSegments(rs);
      } catch (e) {
        // ignore
      }
    };
    loadSegments();
  }, []);

  const loadAgent = async (id: string) => {
    try {
      setLoading(true);
      const agent = await agentApi.getAgent(id);
      setFormData(agent);
    } catch (error) {
      console.error('Error loading agent:', error);
      toast.error(t('agents.form.errorLoadingAgent'));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleConfigChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      agentConfig: {
        ...prev.agentConfig!,
        [field]: value
      }
    }));
  };

  const openBrainImproveDialog = () => {
    const d = document.createElement('div');
    d.innerHTML = formData.description || '';
    const text = (d.textContent || '').trim();
    setBrainImproveInput(text);
    setBrainImproveDialogOpen(true);
  };

  const handleRequestImprovedBrain = async () => {
    if (!agentId) {
      toast.error(t('agents.form.agentNotCreated'));
      return;
    }
    if (!brainImproveInput.trim()) {
      toast.error(t('agents.form.descriptionRequired'));
      return;
    }
    try {
      setBrainImproveLoading(true);
      const improved = await agentApi.improvePrompt(agentId, brainImproveInput, formData.model || 'gpt-4o-mini');
      setBrainImproveInput(improved);
      toast.success(t('agents.form.descriptionImproved'));
    } catch (e) {
      console.error(e);
      toast.error(t('agents.form.improveDescriptionFailed'));
    } finally {
      setBrainImproveLoading(false);
    }
  };

  const handleApplyImprovedBrain = () => {
    if (!brainImproveInput.trim()) return;
    setFormData(prev => ({ ...prev, description: brainImproveInput }));
    setBrainImproveDialogOpen(false);
  };

  const handleAddPrompt = () => {
    setCurrentPrompt({ role: 'assistant', content: '' });
    setEditingPromptIndex(null);
    setPromptDialog(true);
  };

  const handleEditPrompt = (index: number) => {
    const prompt = formData.prompts![index];
    setCurrentPrompt({ ...prompt, role: 'assistant' });
    setEditingPromptIndex(index);
    setPromptDialog(true);
  };

  const handleSavePrompt = () => {
    if (!currentPrompt.content.trim()) {
      toast.error(t('agents.form.promptContentRequired'));
      return;
    }

    const prompts = [...(formData.prompts || [])];
    const promptToSave: AgentPrompt = { ...currentPrompt, role: 'assistant' };

    if (editingPromptIndex !== null) {
      prompts[editingPromptIndex] = promptToSave;
    } else {
      prompts.push(promptToSave);
    }

    setFormData(prev => ({ ...prev, prompts }));
    setPromptDialog(false);
    setCurrentPrompt({ role: 'assistant', content: '' });
    setEditingPromptIndex(null);
  };

  const handleDeletePrompt = (index: number) => {
    const prompts = formData.prompts?.filter((_, i) => i !== index) || [];
    setFormData(prev => ({ ...prev, prompts }));
  };

  const handleOpenImprovePrompt = (index: number) => {
    const prompt = formData.prompts?.[index];
    if (!prompt) return;
    setImproveTargetIndex(index);
    setImproveInput(prompt.content || '');
    setImproveDialogOpen(true);
  };

  const handleRequestImprovedPrompt = async () => {
    if (!agentId) {
      toast.error(t('agents.form.agentNotCreated'));
      return;
    }
    if (!improveInput.trim()) {
      toast.error(t('agents.form.promptContentRequired'));
      return;
    }
    try {
      setImproveLoading(true);
      const improved = await agentApi.improvePrompt(agentId, improveInput, formData.model || 'gpt-4o-mini');
      setImproveInput(improved);
      toast.success(t('agents.form.promptImproved'));
    } catch (e) {
      console.error(e);
      toast.error(t('agents.form.improvePromptFailed'));
    } finally {
      setImproveLoading(false);
    }
  };

  const handleApplyImprovedPrompt = () => {
    if (improveTargetIndex === null) return;
    const prompts = [...(formData.prompts || [])];
    const target = prompts[improveTargetIndex];
    if (!target) return;
    prompts[improveTargetIndex] = { ...target, content: improveInput };
    setFormData(prev => ({ ...prev, prompts }));
    setImproveDialogOpen(false);
    setImproveTargetIndex(null);
    setImproveInput('');
  };

  const validateForm = (): string | null => {
    const brainText = formData.description || '';
    const brainTextContent = brainText.replace(/<[^>]*>/g, '');
    if (brainTextContent.length > 25000) {
      return t('agents.form.brainLimitExceeded', { count: brainTextContent.length });
    }

    const prompts = formData.prompts || [];
    if (prompts.length > 0) {
      const allPromptsText = prompts.map(p => p.content).join(' ');
      const totalTokenInfo = calculateTokenInfo(allPromptsText, 200000);

      if (totalTokenInfo.isOverLimit) {
        return t('agents.form.promptsLimitExceeded', { tokens: totalTokenInfo.tokens, maxTokens: totalTokenInfo.maxTokens });
      }
    }

    return null;
  };

  const canSaveForm = (): boolean => {
    if (!formData.name?.trim()) return false;

    const descriptionText = (() => {
      const d = document.createElement('div');
      d.innerHTML = formData.description || '';
      return (d.textContent || '').trim();
    })();
    if (!descriptionText) return false;

    return validateForm() === null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name?.trim()) {
      toast.error(t('agents.form.nameRequired'));
      return;
    }

    const descriptionText = (() => {
      const d = document.createElement('div');
      d.innerHTML = formData.description || '';
      return (d.textContent || '').trim();
    })();
    if (!descriptionText) {
      toast.error(t('agents.form.descriptionRequired'));
      return;
    }

    const validationError = validateForm();
    if (validationError) {
      toast.error(validationError);
      return;
    }

    try {
      setSaving(true);

      const agentData = {
        ...formData,
        userId: user?.id || '',
      };

      let result: Agent;
      if (isEditing && agentId) {
        result = await agentApi.updateAgent(agentId, agentData);
      } else {
        result = await agentApi.createAgent(agentData);
      }

      onSave(result);
    } catch (error) {
      console.error('Error saving agent:', error);
      toast.error(isEditing ? t('agents.form.errorUpdatingAgent') : t('agents.form.errorCreatingAgent'));
    } finally {
      setSaving(false);
    }
  };

  // Render section modal content
  const renderModalContent = (sectionId: string) => {
    switch (sectionId) {
      case 'basic-info':
        return (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">{t('agents.form.basicInfoTitle')}</Typography>
              <IconButton size="small" onClick={() => { setHelpTopic('basic-info'); setHelpOpen(true); }}>
                <HelpOutlineIcon />
              </IconButton>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '2fr 1fr 1fr 1fr' }, gap: 2 }}>
              <TextField
                fullWidth
                label={t('agents.form.agentName')}
                value={formData.name || ''}
                onChange={(e) => handleInputChange('name', e.target.value)}
                required
              />
              <FormControl fullWidth>
                <InputLabel>{t('agents.status')}</InputLabel>
                <Select
                  value={formData.status || 'Enabled'}
                  onChange={(e) => handleInputChange('status', e.target.value as AgentStatus)}
                >
                  <MenuItem value="Enabled">{t('agents.active')}</MenuItem>
                  <MenuItem value="Disabled">{t('agents.inactive')}</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>{t('agents.form.model')}</InputLabel>
                <Select
                  value={formData.model || 'gpt-4o-mini'}
                  onChange={(e) => handleInputChange('model', e.target.value as AgentModel)}
                >
                  {AI_MODELS.map((model) => (
                    <MenuItem key={model.value} value={model.value} sx={{ display: 'flex', justifyContent: 'space-between', gap: 4 }}>
                      <span style={{ flex: 1 }}>{model.label}</span>
                      <span style={{ color: 'text.secondary', marginLeft: '16px' }}>{model.credits} {model.credits > 1 ? t('pages.credits.credits') : t('pages.credits.credits').slice(0, -1)}</span>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                type="number"
                label={t('agents.form.responseDelay')}
                value={formData.responseDelay || 0}
                onChange={(e) => handleInputChange('responseDelay', parseInt(e.target.value) || 0)}
              />
            </Box>
          </Box>
        );

      case 'brain':
        return (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="h6">{t('agents.form.brainTitle')}</Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <IconButton size="small" onClick={() => { setHelpTopic('brain'); setHelpOpen(true); }}>
                  <HelpOutlineIcon />
                </IconButton>
                <Tooltip title={t('agents.form.improveDescription')}>
                  <span>
                    <Button variant="outlined" size="small" startIcon={<AutoFixHighIcon />} onClick={openBrainImproveDialog}>
                      {t('agents.form.improveDescription')}
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr' }, gap: 2 }}>
              <RichTextEditor
                label={t('agents.form.description')}
                value={formData.description || ''}
                onChange={(html) => handleInputChange('description', html)}
                required
                placeholder={t('agents.form.descriptionPlaceholder')}
                maxLength={25000}
                showCharCounter={true}
              />
            </Box>
          </Box>
        );

      case 'prompts':
        return (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
              <Typography variant="h6">
                {t('agents.form.promptsTitle')} ({formData.prompts?.length || 0})
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <IconButton size="small" onClick={() => { setHelpTopic('prompts'); setHelpOpen(true); }}>
                  <HelpOutlineIcon />
                </IconButton>
                <TextField
                  size="small"
                  placeholder={t('agents.form.filterPrompts')}
                  value={promptFilter}
                  onChange={(e) => setPromptFilter(e.target.value)}
                  sx={{ minWidth: { xs: '120px', md: '200px' } }}
                />
                <Button startIcon={<AddIcon />} onClick={handleAddPrompt} variant="outlined" size="small">
                  {t('agents.form.addPrompt')}
                </Button>
              </Box>
            </Box>

            {(() => {
              const allPromptsText = (formData.prompts || []).map(p => p.content).join(' ');
              const totalTokenInfo = calculateTokenInfo(allPromptsText, 200000);
              return (
                <Box sx={{ mb: 2, p: 2, backgroundColor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('agents.form.totalTokens')}</Typography>
                  <TokenCounter text={allPromptsText} maxTokens={200000} showProgress={true} showChip={false} variant="default" />
                </Box>
              );
            })()}

            {(() => {
              const query = promptFilter.trim().toLowerCase();
              const promptsList = formData.prompts || [];
              const filtered = query
                ? promptsList.reduce<{ prompt: AgentPrompt; originalIndex: number; }[]>((acc, p, i) => {
                    const role = (p.role || '').toLowerCase();
                    const content = (p.content || '').toLowerCase();
                    if (role.includes(query) || content.includes(query)) acc.push({ prompt: p, originalIndex: i });
                    return acc;
                  }, [])
                : promptsList.map((p, i) => ({ prompt: p, originalIndex: i }));
              return filtered.map(({ prompt, originalIndex }) => {
                const promptTokenInfo = calculateTokenInfo(prompt.content, 200000);
                const isExpanded = expandedPrompts.has(originalIndex);
                return (
                  <Box key={originalIndex} sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2, mb: 2, transition: 'all 0.2s', '&:hover': { boxShadow: 3, backgroundColor: 'action.hover' } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {isExpanded ? (prompt.content || '') : ((prompt.content || '').length > 200 ? `${(prompt.content || '').substring(0, 200)}...` : (prompt.content || ''))}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title={isExpanded ? t('agents.form.collapse') : t('agents.form.expand')}>
                          <IconButton size="small" onClick={() => setExpandedPrompts(prev => { const next = new Set(prev); if (next.has(originalIndex)) next.delete(originalIndex); else next.add(originalIndex); return next; })}>
                            {isExpanded ? <CloseFullscreenIcon /> : <OpenInFullIcon />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('agents.form.improvePrompt')}>
                          <IconButton size="small" onClick={() => handleOpenImprovePrompt(originalIndex)}>
                            <AutoFixHighIcon />
                          </IconButton>
                        </Tooltip>
                        <IconButton size="small" onClick={() => handleEditPrompt(originalIndex)}>
                          <AddIcon />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDeletePrompt(originalIndex)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Box>
                    <Typography variant="caption" color={promptTokenInfo.isOverLimit ? 'error' : 'text.secondary'}>
                      {promptTokenInfo.characters} {t('agents.form.characters')} | {promptTokenInfo.tokens} {t('agents.form.tokens')}
                    </Typography>
                  </Box>
                );
              });
            })()}
          </Box>
        );

      case 'ai-actions':
        return (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="h6">{t('agents.form.aiActionsTitle')}</Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <IconButton size="small" onClick={() => { setHelpTopic('ai-actions'); setHelpOpen(true); }}>
                  <HelpOutlineIcon />
                </IconButton>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setFormData(prev => ({ ...prev, aiActions: [...(prev.aiActions || []), { type: 'message', instruction: '', description: '', userMessage: '', shouldBlockUser: false, sendFullContext: false, segmentId: undefined }] }))}
                  startIcon={<AddIcon />}
                >
                  {t('agents.form.addAIAction')}
                </Button>
              </Box>
            </Box>
            {(!formData.aiActions || formData.aiActions.length === 0) ? (
              <Typography variant="body2" color="text.secondary">{t('agents.form.noAIActions')}</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {formData.aiActions!.map((action, index) => (
                  <Accordion key={index} defaultExpanded={index === 0} sx={{ borderRadius: 2, '&:before': { display: 'none' }, boxShadow: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography sx={{ flexGrow: 1 }}>{action.instruction || `${t('agents.form.aiActionsTitle')} ${index + 1}`}</Typography>
                      <Chip label={action.type} size="small" />
                      <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); setFormData(prev => ({ ...prev, aiActions: prev.aiActions?.filter((_, i) => i !== index) || [] })); }} sx={{ ml: 1 }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
                        <FormControl fullWidth>
                          <InputLabel>{t('agents.form.actionType')}</InputLabel>
                          <Select label={t('agents.form.actionType')} value={action.type} onChange={(e) => setFormData(prev => ({ ...prev, aiActions: prev.aiActions!.map((a, i) => i === index ? { ...a, type: e.target.value as any } : a) }))}>
                            <MenuItem value="api">API</MenuItem>
                            <MenuItem value="redirect">Redirect</MenuItem>
                            <MenuItem value="message">{t('agents.form.userMessage')}</MenuItem>
                          </Select>
                        </FormControl>
                        <TextField label={t('agents.form.shortDescription')} value={action.instruction || ''} onChange={(e) => setFormData(prev => ({ ...prev, aiActions: prev.aiActions!.map((a, i) => i === index ? { ...a, instruction: e.target.value } : a) }))} fullWidth />
                        <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
                          <TextField label={t('agents.form.aiInstruction')} value={action.description || ''} onChange={(e) => setFormData(prev => ({ ...prev, aiActions: prev.aiActions!.map((a, i) => i === index ? { ...a, description: e.target.value } : a) }))} fullWidth multiline minRows={2} />
                        </Box>
                        {action.type === 'message' && (
                          <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
                            <TextField label={t('agents.form.userMessage')} value={action.userMessage || ''} onChange={(e) => setFormData(prev => ({ ...prev, aiActions: prev.aiActions!.map((a, i) => i === index ? { ...a, userMessage: e.target.value } : a) }))} fullWidth multiline minRows={2} />
                          </Box>
                        )}
                        {action.type === 'redirect' && (
                          <>
                            <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
                              <TextField label={t('agents.form.userMessage')} value={action.userMessage || ''} onChange={(e) => setFormData(prev => ({ ...prev, aiActions: prev.aiActions!.map((a, i) => i === index ? { ...a, userMessage: e.target.value } : a) }))} fullWidth multiline minRows={2} />
                            </Box>
                            <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' }, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
                              <FormControl sx={{ minWidth: {xs: "100%", sm: 400} }}>
                                <InputLabel>{t('users.segment')}</InputLabel>
                                <Select label={t('users.segment')} value={action.segmentId ?? ''} onChange={(e) => setFormData(prev => ({ ...prev, aiActions: prev.aiActions!.map((a, i) => i === index ? { ...a, segmentId: (e.target.value || undefined) as string | undefined } : a) }))}>
                                  <MenuItem value="">{t('users.none')}</MenuItem>
                                  {segments.map(s => (
                                    <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                              <FormControlLabel control={<Switch checked={!!action.sendFullContext} onChange={(e) => setFormData(prev => ({ ...prev, aiActions: prev.aiActions!.map((a, i) => i === index ? { ...a, sendFullContext: e.target.checked } : a) }))} />} label={t('agents.form.sendFullContext')} />
                              <FormControlLabel control={<Switch checked={!!action.shouldBlockUser} onChange={(e) => setFormData(prev => ({ ...prev, aiActions: prev.aiActions!.map((a, i) => i === index ? { ...a, shouldBlockUser: e.target.checked } : a) }))} />} label={t('agents.form.blockUserAfterAction')} />
                            </Box>
                          </>
                        )}
                        {action.type === 'api' && (
                          <>
                            <TextField label={t('agents.form.apiUrl')} value={action.apiUrl || ''} onChange={(e) => setFormData(prev => ({ ...prev, aiActions: prev.aiActions!.map((a, i) => i === index ? { ...a, apiUrl: e.target.value } : a) }))} fullWidth />
                            <TextField label={t('agents.form.token')} value={action.token || ''} onChange={(e) => setFormData(prev => ({ ...prev, aiActions: prev.aiActions!.map((a, i) => i === index ? { ...a, token: e.target.value } : a) }))} fullWidth />
                            <FormControlLabel control={<Switch checked={!!action.sendFullContext} onChange={(e) => setFormData(prev => ({ ...prev, aiActions: prev.aiActions!.map((a, i) => i === index ? { ...a, sendFullContext: e.target.checked } : a) }))} />} label={t('agents.form.sendFullContext')} />
                          </>
                        )}
                        {action.type !== 'redirect' && (
                          <FormControlLabel control={<Switch checked={!!action.shouldBlockUser} onChange={(e) => setFormData(prev => ({ ...prev, aiActions: prev.aiActions!.map((a, i) => i === index ? { ...a, shouldBlockUser: e.target.checked } : a) }))} />} label={t('agents.form.blockUserAfterAction')} />
                        )}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            )}
          </Box>
        );

      case 'scheduling':
        return (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="h6">{t('agents.form.schedulingTitle')}</Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <IconButton size="small" onClick={() => { setHelpTopic('schedule'); setHelpOpen(true); }}>
                  <HelpOutlineIcon />
                </IconButton>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setFormData(prev => ({ ...prev, randomicoSchedule: [...(prev.randomicoSchedule || []), { dayOfWeek: 1, startTime: '09:00', endTime: '17:00', disabledIA: false, statusRandomicoSchedule: true, ClienteAssumeRandomico: false } as RandomicoSchedule] }))}
                  startIcon={<AddIcon />}
                >
                  {t('agents.form.addSchedule')}
                </Button>
              </Box>
            </Box>
            {(!formData.randomicoSchedule || formData.randomicoSchedule.length === 0) ? (
              <Typography variant="body2" color="text.secondary">{t('agents.form.noSchedule')}</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {formData.randomicoSchedule!.map((sched, index) => (
                  <Paper key={index} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                      <Typography variant="subtitle1">{t('agents.form.schedule')} {index + 1}</Typography>
                      <IconButton onClick={() => setFormData(prev => ({ ...prev, randomicoSchedule: prev.randomicoSchedule?.filter((_, i) => i !== index) || [] }))} size="small" color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
                      <FormControl fullWidth>
                        <InputLabel>{t('agents.form.dayOfWeek')}</InputLabel>
                        <Select label={t('agents.form.dayOfWeek')} value={sched.dayOfWeek ?? ''} onChange={(e) => setFormData(prev => ({ ...prev, randomicoSchedule: prev.randomicoSchedule!.map((s, i) => i === index ? { ...s, dayOfWeek: Number(e.target.value) } : s) }))}>
                          <MenuItem value={0}>{t('agents.form.sunday')}</MenuItem>
                          <MenuItem value={1}>{t('agents.form.monday')}</MenuItem>
                          <MenuItem value={2}>{t('agents.form.tuesday')}</MenuItem>
                          <MenuItem value={3}>{t('agents.form.wednesday')}</MenuItem>
                          <MenuItem value={4}>{t('agents.form.thursday')}</MenuItem>
                          <MenuItem value={5}>{t('agents.form.friday')}</MenuItem>
                          <MenuItem value={6}>{t('agents.form.saturday')}</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField label={t('agents.form.startTime')} type="time" value={sched.startTime || ''} onChange={(e) => setFormData(prev => ({ ...prev, randomicoSchedule: prev.randomicoSchedule!.map((s, i) => i === index ? { ...s, startTime: e.target.value } : s) }))} InputLabelProps={{ shrink: true }} fullWidth />
                      <TextField label={t('agents.form.endTime')} type="time" value={sched.endTime || ''} onChange={(e) => setFormData(prev => ({ ...prev, randomicoSchedule: prev.randomicoSchedule!.map((s, i) => i === index ? { ...s, endTime: e.target.value } : s) }))} InputLabelProps={{ shrink: true }} fullWidth />
                    </Box>
                    <Box sx={{ mt: 1.5, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
                      <FormControlLabel control={<Switch checked={!!sched.statusRandomicoSchedule} onChange={(e) => setFormData(prev => ({ ...prev, randomicoSchedule: prev.randomicoSchedule!.map((s, i) => i === index ? { ...s, statusRandomicoSchedule: e.target.checked } : s) }))} />} label={t('agents.form.scheduleStatus')} />
                      <FormControlLabel control={<Switch checked={!!sched.disabledIA} onChange={(e) => setFormData(prev => ({ ...prev, randomicoSchedule: prev.randomicoSchedule!.map((s, i) => i === index ? { ...s, disabledIA: e.target.checked } : s) }))} />} label={t('agents.form.aiDisabled')} />
                      <FormControlLabel control={<Switch checked={!!sched.clienteAssumeRandomico} onChange={(e) => setFormData(prev => ({ ...prev, randomicoSchedule: prev.randomicoSchedule!.map((s, i) => i === index ? { ...s, clienteAssumeRandomico: e.target.checked } : s) }))} />} label={t('agents.form.clientAssumesRandom')} />
                    </Box>
                  </Paper>
                ))}
              </Box>
            )}
          </Box>
        );

      case 'settings':
        return (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">{t('agents.form.settingsTitle')}</Typography>
              <IconButton size="small" onClick={() => { setHelpTopic('settings'); setHelpOpen(true); }}>
                <HelpOutlineIcon />
              </IconButton>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>{t('agents.form.routingMode')}</InputLabel>
                <Select label={t('agents.form.routingMode')} value={formData.agentConfig?.routingMode || 'disabled'} onChange={(e) => handleConfigChange('routingMode', e.target.value)}>
                  <MenuItem value="disabled">{t('agents.form.routingDisabled')}</MenuItem>
                  <MenuItem value="Atendente">{t('agents.form.routingAttendant')}</MenuItem>
                  <MenuItem value="Agendamento">{t('agents.form.routingSchedule')}</MenuItem>
                  <MenuItem value="Todos">{t('agents.form.routingAll')}</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>{t('agents.form.followUpTime')}</InputLabel>
                <Select label={t('agents.form.followUpTime')} value={formData.agentConfig?.followUp || 0} onChange={(e) => handleConfigChange('followUp', Number(e.target.value))}>
                  <MenuItem value={0}>{t('agents.form.followUpDisabled')}</MenuItem>
                  <MenuItem value={5}>{t('agents.form.followUp5min')}</MenuItem>
                  <MenuItem value={15}>{t('agents.form.followUp15min')}</MenuItem>
                  <MenuItem value={20}>{t('agents.form.followUp20min')}</MenuItem>
                  <MenuItem value={30}>{t('agents.form.followUp30min')}</MenuItem>
                  <MenuItem value={60}>{t('agents.form.followUp1hour')}</MenuItem>
                  <MenuItem value={120}>{t('agents.form.followUp2hours')}</MenuItem>
                  <MenuItem value={720}>{t('agents.form.followUp12hours')}</MenuItem>
                  <MenuItem value={1380}>{t('agents.form.followUp23hours')}</MenuItem>
                  <MenuItem value={1410}>{t('agents.form.followUp23hours30min')}</MenuItem>
                  <MenuItem value={1440}>{t('agents.form.followUp24hours')}</MenuItem>
                </Select>
              </FormControl>
              {formData.agentConfig?.routingMode && formData.agentConfig.routingMode !== 'disabled' && (
                <>
                  <TextField fullWidth label={t('agents.form.attendedLeadDescription')} value={formData.agentConfig?.descriptionLeedAtendido || ''} onChange={(e) => handleConfigChange('descriptionLeedAtendido', e.target.value)} multiline rows={3} />
                  <TextField fullWidth label={t('agents.form.returnLeadDescription')} value={formData.agentConfig?.descriptionLeedRetorno || ''} onChange={(e) => handleConfigChange('descriptionLeedRetorno', e.target.value)} multiline rows={3} />
                </>
              )}
              {formData.agentConfig?.followUp && formData.agentConfig.followUp !== 0 && (
                <TextField fullWidth label={t('agents.form.followUpDescription')} value={formData.agentConfig?.followUpDescription || ''} onChange={(e) => handleConfigChange('followUpDescription', e.target.value)} multiline rows={3} sx={{ gridColumn: { xs: '1', md: '1 / -1' } }} />
              )}
              <FormControlLabel control={<Switch checked={!!formData.agentConfig?.sendBussinessHours} onChange={(e) => handleConfigChange('sendBussinessHours', e.target.checked)} />} label={t('agents.form.sendBusinessHours')} />
              <Divider sx={{ gridColumn: { xs: '1', md: '1 / -1' } }} />
              <TextField fullWidth label={t('agents.form.randomDisabledMessage')} value={formData.mensagemRetornoRandomicoDisabled || ''} onChange={(e) => handleInputChange('mensagemRetornoRandomicoDisabled', e.target.value)} multiline rows={3} sx={{ gridColumn: { xs: '1', md: '1 / -1' } }} />
            </Box>
          </Box>
        );

      case 'review':
        return (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">{t('agents.form.reviewTitle')}</Typography>
              <IconButton size="small" onClick={() => { setHelpTopic('review'); setHelpOpen(true); }}>
                <HelpOutlineIcon />
              </IconButton>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('agents.form.reviewDescription')}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
              <TextField label={t('common.name')} value={formData.name || ''} InputProps={{ readOnly: true }} />
              <TextField label={t('agents.status')} value={formData.status || 'Enabled'} InputProps={{ readOnly: true }} />
              <TextField label={t('agents.form.model')} value={formData.model || ''} InputProps={{ readOnly: true }} />
              <TextField label={t('agents.form.productType')} value={formData.typeProduct || ''} InputProps={{ readOnly: true }} />
              <TextField label={t('agents.form.responseDelay')} value={String(formData.responseDelay || 0)} InputProps={{ readOnly: true }} />
              <TextField label={t('agents.form.promptsCount')} value={String(formData.prompts?.length || 0)} InputProps={{ readOnly: true }} />
              <TextField label={t('agents.form.aiActionsCount')} value={String(formData.aiActions?.length || 0)} InputProps={{ readOnly: true }} />
              <TextField label={t('agents.form.schedulesCount')} value={String(formData.randomicoSchedule?.length || 0)} InputProps={{ readOnly: true }} />
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      animation: 'fadeIn 0.5s ease-in-out',
      '@keyframes fadeIn': {
        from: { opacity: 0, transform: 'translateY(10px)' },
        to: { opacity: 1, transform: 'translateY(0)' }
      }
    }}>
      <form onSubmit={handleSubmit} style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Validation Alert */}
        {(() => {
          const validationError = validateForm();
          if (validationError) {
            return (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 2,
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(244, 67, 54, 0.3)' : 'rgba(244, 67, 54, 0.2)'}`,
                  background: theme.palette.mode === 'dark' 
                    ? 'linear-gradient(135deg, rgba(244, 67, 54, 0.15) 0%, rgba(244, 67, 54, 0.05) 100%)'
                    : 'linear-gradient(135deg, rgba(244, 67, 54, 0.08) 0%, rgba(244, 67, 54, 0.03) 100%)',
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {t('agents.form.cannotSave')}
                </Typography>
                <Typography variant="body2">{validationError}</Typography>
              </Alert>
            );
          }
          return null;
        })()}

        {/* Graph Canvas */}
        <GraphCanvas sx={{ 
          flex: 1, 
          mb: 3,
          animation: 'canvasFadeIn 0.6s ease-in-out 0.1s both',
          '@keyframes canvasFadeIn': {
            from: { opacity: 0, transform: 'scale(0.98)' },
            to: { opacity: 1, transform: 'scale(1)' }
          }
        }}>

          {/* Nodes */}
          {nodesConfig.map((node) => {
            const badge = node.getBadge();
            const completed = isSectionCompleted(node.id);
            return (
              <GraphNode
                key={node.id}
                color={node.color}
                required={node.required}
                isEditing={isEditing}
                completed={completed}
                onClick={() => setOpenModal(node.id)}
                sx={{
                  left: `${node.position.x}%`,
                  top: `${node.position.y}%`,
                  transform: 'translate(-50%, -50%)',
                }}
              >
                <Box className="node-icon-container">
                  {React.cloneElement(node.icon, { className: 'node-icon' })}
                </Box>
                <Typography className="node-label">{node.label}</Typography>
                {badge !== null && badge !== undefined && (
                  <Box className="node-badge">
                    <Badge
                      badgeContent={badge}
                      sx={{
                        '& .MuiBadge-badge': {
                          backgroundColor: node.color,
                          color: 'white',
                          fontWeight: 700,
                          fontSize: '0.75rem',
                          width: 28,
                          height: 28,
                          minWidth: 28,
                        },
                      }}
                    />
                  </Box>
                )}
              </GraphNode>
            );
          })}
        </GraphCanvas>

        {/* Action Buttons */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          gap: 2, 
          mt: 'auto', 
          pt: 3,
          borderTop: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {formData.name || t('agents.form.newAgent')}
            </Typography>
            {agentId && (
              <Chip 
                label={isEditing ? t('agents.form.editing') : t('agents.form.new')} 
                size="small" 
                color="primary" 
                variant="outlined"
                sx={{ 
                  height: 20,
                  fontSize: '0.7rem',
                  fontWeight: 600
                }}
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button 
              variant="outlined" 
              onClick={onCancel} 
              disabled={saving} 
              startIcon={<CloseIcon />}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: { xs: 1, sm: 3 },
                borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                '&:hover': {
                  borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)',
                  background: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                },
                '& .MuiButton-startIcon': {
                  margin: { xs: 0, sm: '0 8px 0 -4px' }
                }
              }}
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                {t('agents.form.cancel')}
              </Box>
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
              disabled={saving || !canSaveForm()}
              sx={{ 
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: { xs: 1, sm: 4 },
                color: theme.palette.mode === 'dark' ? '#000' : undefined,
                '&:hover': {
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.3s ease',
                '& .MuiButton-startIcon': {
                  margin: { xs: 0, sm: '0 8px 0 -4px' }
                }
              }}
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                {saving ? t('agents.form.saving') : (isEditing ? t('agents.form.updateAgent') : t('agents.form.createAgent'))}
              </Box>
            </Button>
          </Box>
        </Box>
      </form>

      {/* Section Modals */}
      {nodesConfig.map((node) => (
        <Dialog
          key={node.id}
          open={openModal === node.id}
          onClose={() => setOpenModal(null)}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              maxHeight: '90vh',
              borderRadius: 3,
              background: theme.palette.mode === 'dark'
                ? '#313131'
                : '#ffffff',
              border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`,
              boxShadow: theme.palette.mode === 'dark'
                ? `0 24px 64px rgba(0, 0, 0, 0.5), 0 0 0 1px ${node.color}20`
                : `0 24px 64px rgba(0, 0, 0, 0.12), 0 0 0 1px ${node.color}15`,
              overflow: 'hidden',
            },
          }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              pb: 2.5,
              pt: 3,
              px: 3,
              background: theme.palette.mode === 'dark'
                ? `linear-gradient(to right, ${node.color}12 0%, transparent 100%)`
                : `linear-gradient(to right, ${node.color}08 0%, transparent 100%)`,
              borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.06)'}`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 2.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: `linear-gradient(135deg, ${node.color}25, ${node.color}15)`,
                  boxShadow: `0 4px 16px ${node.color}30`,
                }}
              >
                {React.cloneElement(node.icon, { 
                  sx: { 
                    fontSize: 28,
                    color: node.color,
                    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
                  } 
                })}
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, fontSize: '1.25rem' }}>
                  {node.label}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                  {t('agents.form.configureSection')}
                </Typography>
              </Box>
            </Box>
            <IconButton
              onClick={() => setOpenModal(null)}
              size="small"
              sx={{
                borderRadius: 1.5,
                width: 36,
                height: 36,
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.05)',
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent
            dividers
            sx={{
              maxHeight: '70vh',
              overflow: 'auto',
              p: 4,
              background: theme.palette.mode === 'dark'
                ? '#1a1a1a'
                : '#fafafa',
              borderTop: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.06)'}`,
              borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.06)'}`,
              '&::-webkit-scrollbar': {
                width: '10px',
              },
              '&::-webkit-scrollbar-track': {
                background: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.03)',
                borderRadius: '10px',
                margin: '8px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: `linear-gradient(180deg, ${node.color}60, ${node.color}40)`,
                borderRadius: '10px',
                border: `2px solid ${theme.palette.mode === 'dark' ? '#1a1a1a' : '#fafafa'}`,
                '&:hover': {
                  background: `linear-gradient(180deg, ${node.color}80, ${node.color}60)`,
                },
              },
            }}
          >
            {renderModalContent(node.id)}
          </DialogContent>
          <DialogActions
            sx={{
              px: 3,
              py: 2.5,
              gap: 1.5,
              borderTop: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.06)'}`,
              background: theme.palette.mode === 'dark'
                ? '#313131'
                : '#ffffff',
            }}
          >
            <Button
              onClick={() => setOpenModal(null)}
              variant="outlined"
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                color: theme.palette.text.primary,
                '&:hover': {
                  borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)',
                  background: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                },
              }}
            >
              {t('agents.form.close')}
            </Button>
            <Button
              onClick={() => {
                setOpenModal(null);
                if (isSectionCompleted(node.id)) {
                  toast.success(t('agents.form.sectionSaved', { section: node.label }));
                }
              }}
              variant="contained"
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: 4,
                color: theme.palette.mode === 'dark' ? '#000' : undefined,
                '&:hover': {
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              {t('agents.form.save')}
            </Button>
          </DialogActions>
        </Dialog>
      ))}

      {/* Prompt Dialog */}
      <Dialog 
        open={promptDialog} 
        onClose={() => setPromptDialog(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: theme.palette.mode === 'dark' ? '#313131' : '#ffffff',
            boxShadow: theme.palette.mode === 'dark'
              ? '0 24px 64px rgba(0, 0, 0, 0.5)'
              : '0 24px 64px rgba(0, 0, 0, 0.12)',
          }
        }}
      >
        <DialogTitle sx={{
          pb: 2,
          pt: 3,
          px: 3,
          background: theme.palette.mode === 'dark'
            ? 'linear-gradient(to right, #FF980012 0%, transparent 100%)'
            : 'linear-gradient(to right, #FF980008 0%, transparent 100%)',
          borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.06)'}`,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #FF980025, #FF980015)',
                boxShadow: '0 4px 16px #FF980030',
              }}
            >
              <EditNoteIcon sx={{ fontSize: 24, color: '#FF9800' }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {editingPromptIndex !== null ? t('agents.form.promptDialogTitle') : t('agents.form.newPromptDialogTitle')}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{
          p: 3,
          background: theme.palette.mode === 'dark' ? '#1a1a1a' : '#fafafa',
        }}>
          <TextField
            fullWidth
            multiline
            rows={8}
            sx={{ mt: 2, '& .MuiInputBase-inputMultiline': { resize: 'vertical', overflow: 'auto' } }}
            label={t('agents.form.promptContent')}
            value={currentPrompt.content}
            onChange={(e) => setCurrentPrompt(prev => ({ ...prev, content: e.target.value }))}
            placeholder={t('agents.form.promptContentPlaceholder')}
            error={(() => {
              const existingPromptsText = (formData.prompts || []).map(p => p.content).join(' ');
              const currentText = currentPrompt.content || '';
              const totalText = existingPromptsText + ' ' + currentText;
              const totalTokens = Math.ceil(totalText.length / 4);
              return totalTokens > 200000;
            })()}
            helperText={(() => {
              const existingPromptsText = (formData.prompts || []).map(p => p.content).join(' ');
              const currentText = currentPrompt.content || '';
              const totalText = existingPromptsText + ' ' + currentText;
              const totalTokens = Math.ceil(totalText.length / 4);
              if (totalTokens > 200000) {
                return t('agents.form.tokenLimitExceeded');
              }
              return '';
            })()}
          />
          {(() => {
            const existingPromptsText = (formData.prompts || []).map(p => p.content).join(' ');
            const currentText = currentPrompt.content || '';
            const totalText = existingPromptsText + ' ' + currentText;
            return (
              <Box sx={{ mt: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('agents.form.totalTokensAllPrompts')}</Typography>
                <TokenCounter text={totalText} maxTokens={200000} showProgress={true} showChip={true} />
              </Box>
            );
          })()}
          <FileManager
            promptText={currentPrompt.content || ''}
            onTextChange={(newText) => setCurrentPrompt(prev => ({ ...prev, content: newText }))}
            disabled={false}
            agentId={agentId}
            onAutoSave={async () => {
              if (isEditing && agentId) {
                try {
                  const agentData = {
                    ...formData,
                    userId: user?.id || '',
                    prompts: (() => {
                      const prompts = [...(formData.prompts || [])];
                      if (editingPromptIndex !== null) {
                        prompts[editingPromptIndex] = currentPrompt;
                      }
                      return prompts;
                    })()
                  };
                  await agentApi.updateAgent(agentId, agentData);
                  toast.success(t('agents.form.promptAutoSaved'));
                } catch (error) {
                  console.error('Erro no salvamento automático:', error);
                  toast.error(t('agents.form.errorAutoSaving'));
                }
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{
          px: 3,
          py: 2.5,
          gap: 1.5,
          borderTop: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.06)'}`,
          background: theme.palette.mode === 'dark' ? '#313131' : '#ffffff',
        }}>
          <Button 
            onClick={() => setPromptDialog(false)}
            variant="outlined"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
              '&:hover': {
                borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)',
                background: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
              },
            }}
          >
            {t('agents.form.cancel')}
          </Button>
          <Button 
            onClick={handleSavePrompt} 
            variant="contained" 
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              color: theme.palette.mode === 'dark' ? '#000' : undefined,
              px: 4,
              '&:hover': {
                transform: 'translateY(-1px)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            {t('agents.form.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Brain Improve Dialog */}
      <Dialog 
        open={brainImproveDialogOpen} 
        onClose={() => setBrainImproveDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: theme.palette.mode === 'dark' ? '#313131' : '#ffffff',
            boxShadow: theme.palette.mode === 'dark'
              ? '0 24px 64px rgba(0, 0, 0, 0.5)'
              : '0 24px 64px rgba(0, 0, 0, 0.12)',
          }
        }}
      >
        <DialogTitle sx={{
          pb: 2,
          pt: 3,
          px: 3,
          background: theme.palette.mode === 'dark'
            ? 'linear-gradient(to right, #9C27B012 0%, transparent 100%)'
            : 'linear-gradient(to right, #9C27B008 0%, transparent 100%)',
          borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.06)'}`,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #9C27B025, #9C27B015)',
                boxShadow: '0 4px 16px #9C27B030',
              }}
            >
              <AutoFixHighIcon sx={{ fontSize: 24, color: '#9C27B0' }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {t('agents.form.improveDescriptionTitle')}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{
          p: 3,
          background: theme.palette.mode === 'dark' ? '#1a1a1a' : '#fafafa',
        }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t('agents.form.improveDescriptionHelp')}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={10}
            label={t('agents.form.agentDescription')}
            value={brainImproveInput}
            onChange={(e) => setBrainImproveInput(e.target.value)}
            placeholder={t('agents.form.improveDescriptionPlaceholder')}
          />
        </DialogContent>
        <DialogActions sx={{
          px: 3,
          py: 2.5,
          gap: 1.5,
          borderTop: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.06)'}`,
          background: theme.palette.mode === 'dark' ? '#313131' : '#ffffff',
        }}>
          <Button 
            onClick={() => setBrainImproveDialogOpen(false)}
            variant="outlined"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
              '&:hover': {
                borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)',
                background: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
              },
            }}
          >
            {t('agents.form.cancel')}
          </Button>
          <Button 
            onClick={handleRequestImprovedBrain} 
            disabled={brainImproveLoading} 
            startIcon={brainImproveLoading ? <CircularProgress size={18} /> : <AutoFixHighIcon />}
            variant="outlined"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              borderColor: '#9C27B0',
              color: '#9C27B0',
              '&:hover': {
                borderColor: '#7B1FA2',
                background: '#9C27B008',
              },
            }}
          >
            {brainImproveLoading ? t('agents.form.generating') : t('agents.form.suggestImprovement')}
          </Button>
          <Button 
            onClick={handleApplyImprovedBrain} 
            variant="contained" 
            disabled={!brainImproveInput.trim() || brainImproveLoading}
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              px: 4,
              '&:hover': {
                transform: 'translateY(-1px)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            {t('agents.form.apply')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Prompt Improve Dialog */}
      <Dialog 
        open={improveDialogOpen} 
        onClose={() => setImproveDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: theme.palette.mode === 'dark' ? '#313131' : '#ffffff',
            boxShadow: theme.palette.mode === 'dark'
              ? '0 24px 64px rgba(0, 0, 0, 0.5)'
              : '0 24px 64px rgba(0, 0, 0, 0.12)',
          }
        }}
      >
        <DialogTitle sx={{
          pb: 2,
          pt: 3,
          px: 3,
          background: theme.palette.mode === 'dark'
            ? 'linear-gradient(to right, #FF980012 0%, transparent 100%)'
            : 'linear-gradient(to right, #FF980008 0%, transparent 100%)',
          borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.06)'}`,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #FF980025, #FF980015)',
                boxShadow: '0 4px 16px #FF980030',
              }}
            >
              <AutoFixHighIcon sx={{ fontSize: 24, color: '#FF9800' }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {t('agents.form.improvePromptTitle')}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{
          p: 3,
          background: theme.palette.mode === 'dark' ? '#1a1a1a' : '#fafafa',
        }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t('agents.form.improvePromptHelp')}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={10}
            label={t('agents.form.promptContent')}
            value={improveInput}
            onChange={(e) => setImproveInput(e.target.value)}
            placeholder={t('agents.form.improvePromptPlaceholder')}
            sx={{ '& .MuiInputBase-inputMultiline': { resize: 'vertical', overflow: 'auto' } }}
            error={(() => {
              const existingPromptsText = (formData.prompts || []).map(p => p.content).join(' ');
              const currentText = improveInput || '';
              const totalText = existingPromptsText + ' ' + currentText;
              const totalTokens = Math.ceil(totalText.length / 4);
              return totalTokens > 200000;
            })()}
            helperText={(() => {
              const existingPromptsText = (formData.prompts || []).map(p => p.content).join(' ');
              const currentText = improveInput || '';
              const totalText = existingPromptsText + ' ' + currentText;
              const totalTokens = Math.ceil(totalText.length / 4);
              if (totalTokens > 200000) {
                return t('agents.form.tokenLimitExceeded');
              }
              return '';
            })()}
          />
          {(() => {
            const existingPromptsText = (formData.prompts || []).map(p => p.content).join(' ');
            const currentText = improveInput || '';
            const totalText = existingPromptsText + ' ' + currentText;
            return (
              <Box sx={{ mt: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('agents.form.totalTokensAllPrompts')}</Typography>
                <TokenCounter text={totalText} maxTokens={200000} showProgress={true} showChip={true} />
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{
          px: 3,
          py: 2.5,
          gap: 1.5,
          borderTop: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.06)'}`,
          background: theme.palette.mode === 'dark' ? '#313131' : '#ffffff',
        }}>
          <Button 
            onClick={() => setImproveDialogOpen(false)}
            variant="outlined"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
              '&:hover': {
                borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)',
                background: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
              },
            }}
          >
            {t('agents.form.cancel')}
          </Button>
          <Button 
            onClick={handleRequestImprovedPrompt} 
            disabled={improveLoading} 
            startIcon={improveLoading ? <CircularProgress size={18} /> : <AutoFixHighIcon />}
            variant="outlined"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              borderColor: '#FF9800',
              color: '#FF9800',
              '&:hover': {
                borderColor: '#F57C00',
                background: '#FF980008',
              },
            }}
          >
            {improveLoading ? t('agents.form.generating') : t('agents.form.suggestImprovement')}
          </Button>
          <Button 
            onClick={handleApplyImprovedPrompt} 
            variant="contained" 
            disabled={!improveInput.trim() || improveLoading}
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              px: 4,
              '&:hover': {
                transform: 'translateY(-1px)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            {t('agents.form.apply')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Help Modal */}
      <AgentHelpModal open={helpOpen} onClose={() => setHelpOpen(false)} topic={helpTopic} />
    </Box>
  );
};

export default AgentForm;
